import { z } from 'zod'

export const AdminHostingStatus = z.enum(['provisioning', 'active', 'failed', 'suspended', 'deprovisioned'])
export type AdminHostingStatus = z.infer<typeof AdminHostingStatus>

export const AdminHostingSiteRow = z.object({
  id: z.number().int(),
  name: z.string(),
  status: AdminHostingStatus,
  runtime: z.string(),
  web_server: z.string(),
  storage_gi: z.number().int(),
  quota_bytes: z.number(),
  // Ent marshals zero int64s with `omitempty`, so the counter fields disappear
  // for freshly imported sites. Default to 0 instead of requiring them.
  bytes_used: z.number().optional().default(0),
  quota_status: z.enum(['normal', 'warning', 'overage']).optional().default('normal'),
  legacy_vhost_id: z.string().nullable().optional(),
  created_at: z.string(),
  provisioned_at: z.string().nullable().optional(),
  account_id: z.number().int(),
  account_email: z.string(),
  subscription_id: z.number().int(),
  product_slug: z.string(),
  primary_domain: z.string().optional().default('')
})
export type AdminHostingSiteRow = z.infer<typeof AdminHostingSiteRow>

export const AdminHostingSiteDomain = z.object({
  id: z.number().int(),
  name: z.string()
})
export type AdminHostingSiteDomain = z.infer<typeof AdminHostingSiteDomain>

export const AdminHostingK8sStatus = z.object({
  phase: z.string(),
  ready: z.boolean(),
  replicas: z.number().int().optional().default(0),
  ready_replicas: z.number().int().optional().default(0),
  message: z.string().optional()
})
export type AdminHostingK8sStatus = z.infer<typeof AdminHostingK8sStatus>

export const AdminHostingDatabaseInfo = z.object({
  name: z.string(),
  type: z.string(),
  ready: z.boolean(),
  db_name: z.string(),
  host: z.string(),
  port: z.number().int(),
  username: z.string(),
  secret_name: z.string()
})
export type AdminHostingDatabaseInfo = z.infer<typeof AdminHostingDatabaseInfo>

export const AdminHostingSiteDetail = AdminHostingSiteRow.extend({
  // Go marshals nil slices to JSON `null` (not `[]`), and `.default()` only
  // fills `undefined` — so tolerate null explicitly and coerce to an empty array.
  domains: z
    .array(AdminHostingSiteDomain)
    .nullish()
    .transform((v) => v ?? []),
  stripe_subscription_id: z.string().nullable().optional(),
  trial_end: z.string().nullable().optional(),
  k8s_status: AdminHostingK8sStatus.nullable().optional(),
  php_config: z.record(z.string(), z.string()).optional(),
  databases: z
    .array(AdminHostingDatabaseInfo)
    .nullish()
    .transform((v) => v ?? []),
  sftp_username: z.string()
})
export type AdminHostingSiteDetail = z.infer<typeof AdminHostingSiteDetail>

export const AdminHostingListResponse = z.object({
  data: z.array(AdminHostingSiteRow),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int()
})
export type AdminHostingListResponse = z.infer<typeof AdminHostingListResponse>
